import React, { useEffect, useState } from "react";
import { Button, Fade, Form, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { BiCheck, BiMinus } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useAppDispatch, useAppSelector } from "../../../app/hook";
import {
  useGetPlanInfoQuery,
  useRedirectToPayModMutation,
  useValidatePromotionCodeMutation
} from "../../../app/services/billing-rtk.service";
import { ChatbotRTKService } from "../../../app/services/chatbot-rtk.services";
import LoadingComponent from "../../../components/common/loading/loading.component";
import ApiFailure from "../../../components/ui/ApiFailure";
import { MultiLanguageUrl } from "../../../constants/multiLanguage.constant";
import { ApsumoPlans } from "../../../constants/plans.constant";
import {
  selectIsUserNew,
  setIsUserNew
} from "../../../features/auth/auth.slice";
import {
  selectChosenPlan,
  selectCurrency,
  selectCurrentPackage
} from "../../../features/dashboard.slice";
import usePermission from "../../../hooks/usePermission";
import { getCookie } from "../../../providers/CookieManagementProvider";

interface IPlan {
  type: "monthly" | "annually";
  name: string;
  code: { USD: string; EUR: string };
  price: {
    monthly: number | null;
    annually: number | null;
  };
  actionBtnText: string;
  features: FeaturesType[];
  level: number;
}

export interface ApiPlan {
  name: string;
  histories?: {
    paymentDateTime: string;
    paymentStatusEnum: string;
    paymentDetailMap: {
      invoice_id: string;
    };
    paymentNextDate: string;
  }[];
  type: string;
  details?: {
    plan_name: string;
    start_date: string;
    end_date: string;
    is_active: boolean;
  };
}

export interface PlanLevel {
  name: string;
  level: number;
}

type CategoryTypes =
  | "Conversations per month"
  | "Creations"
  | "AI Models"
  | "Voice Features"
  | "Settings"
  | "Customization"
  | "Automation Tools"
  | "Analytics";

type FeaturesType = {
  name: string;
  category: CategoryTypes;
  valueType: "string" | "boolean";
  value?: string | boolean;
};

export enum EPlanCodes {
  FREE = "CHATBOT_FREE",
  FREE_EU = "CHABOTC_LSH3RKBR5W",
  BASIC_MONTHLY = "CHATBOT_BASIC_MONTHLY",
  BASIC_MONTHLY_EU = "CHABOM_YXN1LILRWY",
  BASIC_ANNUAL = "CHATBOT_BASIC_ANNUAL",
  BASIC_ANNUAL_EU = "CHABOA_02OW2E8C4U",
  BASIC_ANNUAL_TRIAL = "CHATBOT_BASIC_ANNUAL_TRIAL",
  ADVANCED_MONTHLY = "CHATBOT_ADVANCED_MONTHLY",
  ADVANCED_MONTHLY_EU = "CHABOM_OSMFCYDT0V",
  ADVANCED_ANNUAL = "CHATBOT_ADVANCED_ANNUAL",
  ADVANCED_ANNUAL_EU = "CHABOA_B42CDBJP56",
  ADVANCED_ANNUAL_TRIAL = "CHATBOT_ADVANCED_ANNUAL_TRIAL",
  ENTERPRISE_ANNUAL_EU = "CHABOA_Y8O3FH61GW",
  ENTERPRISE_ANNUAL = "CHABOM_5Q5FNDRELE",
  ENTERPRISE_MONTHLY_EU = "CHABOM_TX5B3V1J9V",
  ENTERPRISE_MONTHLY = "CHABOM_8F83AHUURU",

  APPSUMO_TIER_1 = "LB_APPSUMO_TIER1",
  APPSUMO_TIER_2 = "LB_APPSUMO_TIER2",
  APPSUMO_TIER_3 = "LB_APPSUMO_TIER3"
}

export const getHighestValidPlan = (
  plans: ApiPlan[],
  planLevels: PlanLevel[]
): PlanLevel | null => {
  const currentDate = new Date();

  const validPlans = plans.filter((plan) => {
    if (plan.type === "SUBSCRIPTION") {
      const validHistory = plan.histories?.find((history) => {
        return (
          history.paymentStatusEnum === "ACTIVE" ||
          history.paymentStatusEnum === "SUCCEEDED" ||
          (history.paymentStatusEnum === "CANCELED" &&
            new Date(history.paymentNextDate) > currentDate)
        );
      });
      return !!validHistory;
    }
  });

  const validPlanLevels = planLevels.filter((level) =>
    validPlans.some((plan) => plan.name === level.name)
  );

  if (validPlanLevels.length === 0) {
    return null;
  }

  return validPlanLevels.reduce((highest, current) =>
    highest.level > current.level ? highest : current
  );
};

export const planLevelsArray = [
  { name: EPlanCodes.FREE, level: 1 },
  { name: EPlanCodes.FREE_EU, level: 1 },
  { name: EPlanCodes.BASIC_MONTHLY, level: 2 },
  { name: EPlanCodes.BASIC_MONTHLY_EU, level: 2 },
  { name: EPlanCodes.BASIC_ANNUAL, level: 3 },
  { name: EPlanCodes.BASIC_ANNUAL_EU, level: 3 },
  { name: EPlanCodes.ADVANCED_MONTHLY, level: 4 },
  { name: EPlanCodes.ADVANCED_MONTHLY_EU, level: 4 },
  { name: EPlanCodes.ADVANCED_ANNUAL, level: 5 },
  { name: EPlanCodes.ADVANCED_ANNUAL_EU, level: 5 },
  { name: EPlanCodes.ENTERPRISE_MONTHLY, level: 6 },
  { name: EPlanCodes.ENTERPRISE_MONTHLY_EU, level: 6 },
  { name: EPlanCodes.ENTERPRISE_ANNUAL, level: 7 },
  { name: EPlanCodes.ENTERPRISE_ANNUAL_EU, level: 7 }
];

const BillingCreatePage = () => {
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const isUserNew = useAppSelector(selectIsUserNew);
  const currentPackage = useAppSelector(selectCurrentPackage);
  const choosenPlanFromRegister = useAppSelector(selectChosenPlan);
  const currencySelected = useAppSelector(selectCurrency);

  const [planType, setPlanType] = useState<"monthly" | "annually">("annually");
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [hoveredIndex, sethoveredIndex] = useState(2);
  const permissions = usePermission();
  const [promoCode, setPromoCode] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [currency, setCurrency] = useState<"USD" | "EUR">(
    i18n.language.startsWith("en") ? "USD" : "EUR"
  );

  useEffect(() => {
    if (currencySelected) {
      if (currencySelected == "EU") {
        setCurrency("EUR");
      } else {
        setCurrency("USD");
      }
    }
  }, [currencySelected]);

  const categories = [
    { label: t("Pricing/key1"), value: "Conversations per month" },
    { label: t("Pricing/key2"), value: "Creations" },
    { label: t("Pricing/key3"), value: "AI Models" },
    { label: t("Pricing/key46"), value: "Automation Tools" },
    { label: t("Pricing/key45"), value: "Voice Features" },
    { label: t("Pricing/key4"), value: "Settings" },
    { label: t("Pricing/key5"), value: "Customization" },
    { label: t("Pricing/key58"), value: "Analytics" }
  ];

  const initialPlans: IPlan[] = [
    {
      type: "monthly",
      name: t("Pricing/key6"),
      code: { USD: EPlanCodes.FREE, EUR: EPlanCodes.FREE_EU },
      price: {
        monthly: 0,
        annually: 0
      },
      actionBtnText: t("Pricing/key34"),
      features: [
        {
          name: t("Pricing/key10"),
          category: "Conversations per month",
          valueType: "string",
          value: "100"
        },
        {
          name: t("Pricing/key11"),
          category: "Conversations per month",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key12"),
          category: "Creations",
          valueType: "string",
          value: "1"
        },
        {
          name: t("Pricing/key13"),
          category: "Creations",
          valueType: "string",
          value: "1"
        },
        {
          name: t("Pricing/key14"),
          category: "Creations",
          valueType: "string",
          value: "5"
        },
        {
          name: t("Pricing/key15"),
          category: "Creations",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key16"),
          category: "Creations",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key17"),
          category: "Creations",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key51"),
          category: "Creations",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key52"),
          category: "Creations",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key18"),
          category: "AI Models",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key19"),
          category: "AI Models",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key20"),
          category: "AI Models",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key21"),
          category: "AI Models",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key22"),
          category: "Voice Features",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key23"),
          category: "Voice Features",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key24"),
          category: "Settings",
          valueType: "string",
          value: "1"
        },
        {
          name: t("Pricing/key25"),
          category: "Settings",
          valueType: "string",
          value: "1"
        },
        {
          name: t("Pricing/key61"),
          category: "Settings",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key57"),
          category: "Settings",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key56"),
          category: "Settings",
          valueType: "string",
          value: "72h"
        },
        {
          name: t("Pricing/key57"),
          category: "Settings",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key26"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key62"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key27"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key63"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key28"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key29"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key30"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key47"),
          category: "Automation Tools",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key48"),
          category: "Automation Tools",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key49"),
          category: "Automation Tools",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key50"),
          category: "Automation Tools",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key59"),
          category: "Analytics",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key60"),
          category: "Analytics",
          valueType: "boolean",
          value: false
        }
      ],
      level: 1
    },
    {
      type: "annually",
      name: t("Pricing/key6"),
      code: { USD: EPlanCodes.FREE, EUR: EPlanCodes.FREE_EU },
      price: {
        monthly: 0,
        annually: 0
      },
      actionBtnText: t("Pricing/key34"),
      features: [
        {
          name: t("Pricing/key10"),
          category: "Conversations per month",
          valueType: "string",
          value: "100"
        },
        {
          name: t("Pricing/key11"),
          category: "Conversations per month",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key12"),
          category: "Creations",
          valueType: "string",
          value: "1"
        },
        {
          name: t("Pricing/key13"),
          category: "Creations",
          valueType: "string",
          value: "1"
        },
        {
          name: t("Pricing/key14"),
          category: "Creations",
          valueType: "string",
          value: "5"
        },
        {
          name: t("Pricing/key15"),
          category: "Creations",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key16"),
          category: "Creations",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key17"),
          category: "Creations",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key51"),
          category: "Creations",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key52"),
          category: "Creations",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key18"),
          category: "AI Models",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key19"),
          category: "AI Models",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key20"),
          category: "AI Models",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key21"),
          category: "AI Models",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key22"),
          category: "Voice Features",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key23"),
          category: "Voice Features",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key24"),
          category: "Settings",
          valueType: "string",
          value: "1"
        },
        {
          name: t("Pricing/key25"),
          category: "Settings",
          valueType: "string",
          value: "1"
        },
        {
          name: t("Pricing/key61"),
          category: "Settings",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key47"),
          category: "Automation Tools",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key57"),
          category: "Settings",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key26"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key62"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key27"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key63"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key28"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key29"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key30"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key48"),
          category: "Automation Tools",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key49"),
          category: "Automation Tools",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key50"),
          category: "Automation Tools",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key59"),
          category: "Analytics",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key60"),
          category: "Analytics",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key56"),
          category: "Settings",
          valueType: "string",
          value: "72h"
        },
        {
          name: t("Pricing/key57"),
          category: "Settings",
          valueType: "boolean",
          value: false
        }
      ],
      level: 1
    },
    {
      type: "monthly",
      name: t("Pricing/key7"),
      code: { USD: EPlanCodes.BASIC_MONTHLY, EUR: EPlanCodes.BASIC_MONTHLY_EU },
      price: {
        monthly: 19,
        annually: 13
      },
      actionBtnText: t("Pricing/key35"),
      features: [
        {
          name: t("Pricing/key10"),
          category: "Conversations per month",
          valueType: "string",
          value: "500"
        },
        {
          name: t("Pricing/key11"),
          category: "Conversations per month",
          valueType: "string",
          value: "250"
        },
        {
          name: t("Pricing/key12"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key31", { count: 2 })
        },
        {
          name: t("Pricing/key13"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key31", { count: 2 })
        },
        {
          name: t("Pricing/key14"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key31", { count: 10 })
        },
        {
          name: t("Pricing/key15"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key32")
        },
        {
          name: t("Pricing/key16"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key32")
        },
        {
          name: t("Pricing/key17"),
          category: "Creations",
          valueType: "string",
          value: "1"
        },
        {
          name: t("Pricing/key51"),
          category: "Creations",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key52"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key53")
        },
        {
          name: t("Pricing/key18"),
          category: "AI Models",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key19"),
          category: "AI Models",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key20"),
          category: "AI Models",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key21"),
          category: "AI Models",
          valueType: "string",
          value: t("Pricing/key33", { count: 4, size: 20 })
        },
        {
          name: t("Pricing/key22"),
          category: "Voice Features",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key23"),
          category: "Voice Features",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key24"),
          category: "Settings",
          valueType: "string",
          value: t("Pricing/key31", { count: 3 })
        },
        {
          name: t("Pricing/key25"),
          category: "Settings",
          valueType: "string",
          value: t("Pricing/key31", { count: 2 })
        },
        {
          name: t("Pricing/key61"),
          category: "Settings",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key47"),
          category: "Automation Tools",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key57"),
          category: "Settings",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key48"),
          category: "Automation Tools",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key49"),
          category: "Automation Tools",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key50"),
          category: "Automation Tools",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key59"),
          category: "Analytics",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key60"),
          category: "Analytics",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key26"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key62"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key27"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key63"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key28"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key29"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key30"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key56"),
          category: "Settings",
          valueType: "string",
          value: "48h"
        },
        {
          name: t("Pricing/key57"),
          category: "Settings",
          valueType: "boolean",
          value: false
        }
      ],
      level: 2
    },
    {
      type: "annually",
      name: t("Pricing/key7"),
      code: { USD: EPlanCodes.BASIC_ANNUAL, EUR: EPlanCodes.BASIC_ANNUAL_EU },
      price: {
        monthly: 19,
        annually: 13
      },
      actionBtnText: t("Pricing/key35"),
      features: [
        {
          name: t("Pricing/key10"),
          category: "Conversations per month",
          valueType: "string",
          value: "500"
        },
        {
          name: t("Pricing/key11"),
          category: "Conversations per month",
          valueType: "string",
          value: "250"
        },
        {
          name: t("Pricing/key12"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key31", { count: 2 })
        },
        {
          name: t("Pricing/key13"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key31", { count: 2 })
        },
        {
          name: t("Pricing/key14"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key31", { count: 10 })
        },
        {
          name: t("Pricing/key15"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key32")
        },
        {
          name: t("Pricing/key16"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key32")
        },
        {
          name: t("Pricing/key17"),
          category: "Creations",
          valueType: "string",
          value: "1"
        },
        {
          name: t("Pricing/key51"),
          category: "Creations",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key52"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key53")
        },
        {
          name: t("Pricing/key18"),
          category: "AI Models",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key19"),
          category: "AI Models",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key20"),
          category: "AI Models",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key21"),
          category: "AI Models",
          valueType: "string",
          value: t("Pricing/key33", { count: 4, size: 20 })
        },
        {
          name: t("Pricing/key22"),
          category: "Voice Features",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key23"),
          category: "Voice Features",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key24"),
          category: "Settings",
          valueType: "string",
          value: t("Pricing/key31", { count: 3 })
        },
        {
          name: t("Pricing/key25"),
          category: "Settings",
          valueType: "string",
          value: t("Pricing/key31", { count: 2 })
        },
        {
          name: t("Pricing/key61"),
          category: "Settings",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key47"),
          category: "Automation Tools",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key48"),
          category: "Automation Tools",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key49"),
          category: "Automation Tools",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key50"),
          category: "Automation Tools",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key59"),
          category: "Analytics",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key60"),
          category: "Analytics",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key26"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key62"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key27"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key63"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key28"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key29"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key30"),
          category: "Customization",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key56"),
          category: "Settings",
          valueType: "string",
          value: "48h"
        },
        {
          name: t("Pricing/key57"),
          category: "Settings",
          valueType: "boolean",
          value: false
        }
      ],
      level: 3
    },
    {
      type: "monthly",
      name: t("Pricing/key8"),
      code: {
        USD: EPlanCodes.ADVANCED_MONTHLY,
        EUR: EPlanCodes.ADVANCED_MONTHLY_EU
      },
      price: {
        monthly: 39,
        annually: 26
      },
      actionBtnText: t("Pricing/key35"),
      features: [
        {
          name: t("Pricing/key10"),
          category: "Conversations per month",
          valueType: "string",
          value: t("Pricing/key42")
        },
        {
          name: t("Pricing/key11"),
          category: "Conversations per month",
          valueType: "string",
          value: "1500"
        },
        {
          name: t("Pricing/key12"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key31", { count: 10 })
        },
        {
          name: t("Pricing/key13"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key31", { count: 10 })
        },
        {
          name: t("Pricing/key14"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key31", { count: 50 })
        },
        {
          name: t("Pricing/key15"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key43")
        },
        {
          name: t("Pricing/key16"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key31", { count: 10 })
        },
        {
          name: t("Pricing/key17"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key31", { count: 5 })
        },
        {
          name: t("Pricing/key51"),
          category: "Creations",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key52"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key54")
        },
        {
          name: t("Pricing/key18"),
          category: "AI Models",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key19"),
          category: "AI Models",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key20"),
          category: "AI Models",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key21"),
          category: "AI Models",
          valueType: "string",
          value: t("Pricing/key33", { count: 10, size: 100 })
        },
        {
          name: t("Pricing/key22"),
          category: "Voice Features",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key23"),
          category: "Voice Features",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key24"),
          category: "Settings",
          valueType: "string",
          value: t("Pricing/key31", { count: 10 })
        },
        {
          name: t("Pricing/key25"),
          category: "Settings",
          valueType: "string",
          value: t("Pricing/key31", { count: 10 })
        },
        {
          name: t("Pricing/key61"),
          category: "Settings",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key47"),
          category: "Automation Tools",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key48"),
          category: "Automation Tools",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key49"),
          category: "Automation Tools",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key50"),
          category: "Automation Tools",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key59"),
          category: "Analytics",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key60"),
          category: "Analytics",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key26"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key62"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key27"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key63"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key28"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key29"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key30"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key56"),
          category: "Settings",
          valueType: "string",
          value: "48h"
        },
        {
          name: t("Pricing/key57"),
          category: "Settings",
          valueType: "boolean",
          value: false
        }
      ],
      level: 4
    },
    {
      type: "annually",
      name: t("Pricing/key8"),
      code: {
        USD: EPlanCodes.ADVANCED_ANNUAL,
        EUR: EPlanCodes.ADVANCED_ANNUAL_EU
      },
      price: {
        monthly: 39,
        annually: 26
      },
      actionBtnText: t("Pricing/key35"),
      features: [
        {
          name: t("Pricing/key10"),
          category: "Conversations per month",
          valueType: "string",
          value: t("Pricing/key42")
        },
        {
          name: t("Pricing/key11"),
          category: "Conversations per month",
          valueType: "string",
          value: "1500"
        },
        {
          name: t("Pricing/key12"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key31", { count: 10 })
        },
        {
          name: t("Pricing/key13"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key31", { count: 10 })
        },
        {
          name: t("Pricing/key14"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key31", { count: 50 })
        },
        {
          name: t("Pricing/key15"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key43")
        },
        {
          name: t("Pricing/key16"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key31", { count: 10 })
        },
        {
          name: t("Pricing/key17"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key31", { count: 5 })
        },
        {
          name: t("Pricing/key51"),
          category: "Creations",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key52"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key54")
        },
        {
          name: t("Pricing/key18"),
          category: "AI Models",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key19"),
          category: "AI Models",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key20"),
          category: "AI Models",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key21"),
          category: "AI Models",
          valueType: "string",
          value: t("Pricing/key33", { count: 10, size: 100 })
        },
        {
          name: t("Pricing/key22"),
          category: "Voice Features",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key23"),
          category: "Voice Features",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key24"),
          category: "Settings",
          valueType: "string",
          value: t("Pricing/key31", { count: 10 })
        },
        {
          name: t("Pricing/key25"),
          category: "Settings",
          valueType: "string",
          value: t("Pricing/key31", { count: 10 })
        },
        {
          name: t("Pricing/key61"),
          category: "Settings",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key47"),
          category: "Automation Tools",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key48"),
          category: "Automation Tools",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key49"),
          category: "Automation Tools",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key50"),
          category: "Automation Tools",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key59"),
          category: "Analytics",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key60"),
          category: "Analytics",
          valueType: "boolean",
          value: false
        },
        {
          name: t("Pricing/key26"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key62"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key27"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key63"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key28"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key29"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key30"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key56"),
          category: "Settings",
          valueType: "string",
          value: "48h"
        },
        {
          name: t("Pricing/key57"),
          category: "Settings",
          valueType: "boolean",
          value: false
        }
      ],
      level: 5
    },
    {
      type: "monthly",
      name: t("Pricing/key9"),
      code: {
        USD: EPlanCodes.ENTERPRISE_MONTHLY,
        EUR: EPlanCodes.ENTERPRISE_MONTHLY_EU
      },
      price: {
        monthly: 370,
        annually: 299
      },
      actionBtnText: t("Pricing/key35"),
      features: [
        {
          name: t("Pricing/key10"),
          category: "Conversations per month",
          valueType: "string",
          value: t("Pricing/key42")
        },
        {
          name: t("Pricing/key11"),
          category: "Conversations per month",
          valueType: "string",
          value: t("Pricing/key44")
        },
        {
          name: t("Pricing/key12"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key42")
        },
        {
          name: t("Pricing/key13"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key42")
        },
        {
          name: t("Pricing/key14"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key42")
        },
        {
          name: t("Pricing/key15"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key42")
        },
        {
          name: t("Pricing/key16"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key42")
        },
        {
          name: t("Pricing/key17"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key42")
        },
        {
          name: t("Pricing/key51"),
          category: "Creations",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key52"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key55")
        },
        {
          name: t("Pricing/key18"),
          category: "AI Models",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key19"),
          category: "AI Models",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key20"),
          category: "AI Models",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key21"),
          category: "AI Models",
          valueType: "string",
          value: t("Pricing/key33", { count: 18, size: 450 })
        },
        {
          name: t("Pricing/key22"),
          category: "Voice Features",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key23"),
          category: "Voice Features",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key24"),
          category: "Settings",
          valueType: "string",
          value: t("Pricing/key42")
        },
        {
          name: t("Pricing/key25"),
          category: "Settings",
          valueType: "string",
          value: t("Pricing/key42")
        },
        {
          name: t("Pricing/key61"),
          category: "Settings",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key47"),
          category: "Automation Tools",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key48"),
          category: "Automation Tools",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key49"),
          category: "Automation Tools",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key50"),
          category: "Automation Tools",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key59"),
          category: "Analytics",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key60"),
          category: "Analytics",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key26"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key62"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key27"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key63"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key28"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key29"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key30"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key56"),
          category: "Settings",
          valueType: "string",
          value: "12h"
        },
        {
          name: t("Pricing/key57"),
          category: "Settings",
          valueType: "boolean",
          value: true
        }
      ],
      level: 6
    },
    {
      type: "annually",
      name: t("Pricing/key9"),
      code: {
        USD: EPlanCodes.ENTERPRISE_ANNUAL,
        EUR: EPlanCodes.ENTERPRISE_ANNUAL_EU
      },
      price: {
        monthly: 370,
        annually: 299
      },
      actionBtnText: t("Pricing/key35"),
      features: [
        {
          name: t("Pricing/key10"),
          category: "Conversations per month",
          valueType: "string",
          value: t("Pricing/key42")
        },
        {
          name: t("Pricing/key11"),
          category: "Conversations per month",
          valueType: "string",
          value: t("Pricing/key44")
        },
        {
          name: t("Pricing/key12"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key42")
        },
        {
          name: t("Pricing/key13"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key42")
        },
        {
          name: t("Pricing/key14"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key42")
        },
        {
          name: t("Pricing/key15"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key42")
        },
        {
          name: t("Pricing/key16"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key42")
        },
        {
          name: t("Pricing/key17"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key42")
        },
        {
          name: t("Pricing/key51"),
          category: "Creations",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key52"),
          category: "Creations",
          valueType: "string",
          value: t("Pricing/key55")
        },
        {
          name: t("Pricing/key18"),
          category: "AI Models",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key19"),
          category: "AI Models",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key20"),
          category: "AI Models",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key21"),
          category: "AI Models",
          valueType: "string",
          value: t("Pricing/key33", { count: 18, size: 450 })
        },
        {
          name: t("Pricing/key22"),
          category: "Voice Features",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key23"),
          category: "Voice Features",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key24"),
          category: "Settings",
          valueType: "string",
          value: t("Pricing/key42")
        },
        {
          name: t("Pricing/key25"),
          category: "Settings",
          valueType: "string",
          value: t("Pricing/key42")
        },
        {
          name: t("Pricing/key61"),
          category: "Settings",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key47"),
          category: "Automation Tools",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key48"),
          category: "Automation Tools",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key49"),
          category: "Automation Tools",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key50"),
          category: "Automation Tools",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key59"),
          category: "Analytics",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key60"),
          category: "Analytics",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key26"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key62"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key27"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key63"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key28"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key29"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key30"),
          category: "Customization",
          valueType: "boolean",
          value: true
        },
        {
          name: t("Pricing/key56"),
          category: "Settings",
          valueType: "string",
          value: "12h"
        },
        {
          name: t("Pricing/key57"),
          category: "Settings",
          valueType: "boolean",
          value: true
        }
      ],
      level: 7
    }
  ];

  const [plans, setPlans] = useState<IPlan[]>(initialPlans);

  const buyPlan = (planName: string) => {
    setIsLoading(planName);

    useRedirectToPayMod({
      plan: planName,
      redirectUrl: `${MultiLanguageUrl[i18n.language] || "en-US"}/verify-plan`
    })
      .unwrap()
      .then((res) => {
        return window.open(res, "_self");
      })
      .catch((err) => {
        toast.error(err.data.message || err.message);
        setIsLoading(null);
        return;
      });
  };

  const getPlanInfo = useGetPlanInfoQuery();

  const [useRedirectToPayMod, useRedirectToPayModStatus] =
    useRedirectToPayModMutation();

  const [vlidatePromotionCode, ValidatePromotionCodeStatus] =
    useValidatePromotionCodeMutation();

  const setHoverIndex = (item: number) => {
    sethoveredIndex(item);
  };

  const checkPromoCode = () => {
    if (promoCode && promoCode != "") {
      vlidatePromotionCode({ code: promoCode })
        .unwrap()
        .then((res) => {
          dispatch(
            ChatbotRTKService.util.invalidateTags([
              "Package",
              "GetAllScenarios"
            ])
          );

          toast.success(t("BillingCreatePage/l-04"));

          dispatch(setIsUserNew(false));

          navigate(
            `/${
              MultiLanguageUrl[i18n.language]
            }/verify-plan?plan_name=${promoCode}&status=true`
          );
        })
        .catch((err) => {
          const codeMessage = err.data.message || err.message;

          if (err.status === 500) {
            toast.error(codeMessage);
          } else if (err.status === 400 && err.data.code == "RSPCKO002") {
            toast.warn(t("BillingCreatePage/RSPCKO002"));
          } else {
            toast.warn(t("BillingCreatePage/l-03"));
          }
        });
    }
  };

  useEffect(() => {
    if (getPlanInfo.data) {
      if (
        getPlanInfo.data.some((dr: any) =>
          ApsumoPlans.some((x) => x.name == dr.name)
        )
      ) {
        window.open("https://appsumo.com/products/louisebot");
        navigate(-1);
        return;
      }
    }
  }, [getPlanInfo.isSuccess]);

  //check permissions
  useEffect(() => {
    if (getPlanInfo.data) {
      let data = [...initialPlans];

      const highestPlan = getHighestValidPlan(
        getPlanInfo.data,
        planLevelsArray
      ) || {
        name: EPlanCodes.FREE,
        level: 1
      };

      // Following if filters plans by their level so user can see accessible plans after each plan upgrade
      if (!isUserNew && permissions?.billingPage && highestPlan) {
        const currentTopPlanLevel = highestPlan.level;

        if (currentTopPlanLevel)
          data =
            currentTopPlanLevel < 7
              ? data.filter((plan) => plan.level > currentTopPlanLevel)
              : data.filter((plan) => plan.level >= currentTopPlanLevel);
      }

      if (choosenPlanFromRegister) {
        const indexOfChoosen = data.findIndex((item) =>
          item.code[currency].startsWith(choosenPlanFromRegister as string)
        );

        if (indexOfChoosen > -1)
          setHoverIndex(indexOfChoosen - (indexOfChoosen > 4 ? 2 : 1));
      } else {
        const filterData = data.filter((dr) => dr.type == planType);
        if (filterData.length <= 2 && hoveredIndex > filterData.length) {
          setHoverIndex(filterData.length);
        }
      }

      setPlans(data);
    }
  }, [getPlanInfo.isSuccess, planType, location.pathname]);

  // set promo code from cookie
  useEffect(() => {
    const cookie = getCookie(
      `${import.meta.env.VITE_STORAGE_PREFIX}_promocode`
    );

    if (cookie) {
      setPromoCode(cookie);
    }
  }, []);

  // Toggle currency based on language changed
  useEffect(() => {
    i18n.on("languageChanged", (lng) => {
      setCurrency(lng.startsWith("en") ? "USD" : "EUR");
    });
  });

  let content;

  if (getPlanInfo.isLoading) content = <LoadingComponent />;

  if (getPlanInfo.isError)
    content = <ApiFailure refetch={getPlanInfo.refetch} />;

  if (getPlanInfo.isSuccess)
    content = (
      <section className="tw-flex tw-flex-col md:tw-gap-5 tw-gap-1 tw-bg-white tw-items-center md:tw-p-0 tw-p-4">
        <div className="tw-container  tw-mt-3  tw-shadow-xl">
          <div className=" p-3 rounded tw-flex tw-flex-col tw-items-center gap-1 tw-bg-white  ">
            {currentPackage && (
              <span className="font-sofiaSans fs-3 tw-font-bold tw-uppercase">
                {t("BillingCreatePage/t-01")} {t(currentPackage?.title)}
              </span>
            )}

            <span className="font-sofiaSans fs-3 tw-font-bold color-forthColor tw-text-center">
              {t("BillingCreatePage/t-02")}
            </span>

            <span className="font-sofiaSans tw-text-blue-800 tw-font-extrabold tw-text-lg tw-text-center">
              {t("BillingCreatePage/t-03")}
            </span>

            <div className="xl:tw-w-[692px] tw-w-[80%]  tw-h-[1px] tw-border tw-my-3 tw-bg-gray-300 tw-border-gray-300"></div>

            <div className="tw-text-center tw-cursor-pointer">
              <div className="tw-flex xl:tw-flex-row tw-flex-col tw-gap-2">
                <span
                  onClick={() => setOpen(!open)}
                  onKeyDown={() => setOpen(!open)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={open}
                  className="tw-text-gray-700 tw-text-lg tw-font-normal tw-font-['Lexend'] tw-leading-normal"
                >
                  {!open && <>{t("BillingCreatePage/t-07")}</>}

                  {open && <>{t("BillingCreatePage/t-09")}</>}
                </span>
                <span
                  onClick={() => setOpen(!open)}
                  onKeyDown={() => setOpen(!open)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={open}
                  className="tw-text-blue-700 tw-text-lg tw-font-semibold tw-font-['Lexend'] tw-leading-7"
                >
                  {!open && <> {t("BillingCreatePage/t-08")}</>}
                </span>
              </div>
              {open && (
                <Fade in={open}>
                  <div
                    id="example-fade-text"
                    className="tw-flex tw-gap-3 tw-justify-end tw-w-full tw-flex-row tw-mt-4"
                  >
                    <Form.Group className="tw-flex-1">
                      <Form.Control
                        value={promoCode as string}
                        placeholder={t("BillingCreatePage/l-05")}
                        onChange={(e: any) => {
                          setPromoCode(e.target.value);
                        }}
                        className="tw-w-full tw-px-4 tw-py-2.5 tw-bg-white
    tw-rounded-md tw-shadow tw-border tw-border-gray-300 placeholder:tw-text-gray-400 tw-text-sm tw-font-normal tw-tw-font-['Lexend'] tw-leading-tight focus:tw-border-black focus:tw-shadow-none"
                      />
                      <Form.Control.Feedback
                        type="invalid"
                        className="d-block font-poppins"
                      ></Form.Control.Feedback>
                    </Form.Group>
                    <Button
                      disabled={ValidatePromotionCodeStatus.isLoading}
                      variant="primary"
                      type="submit"
                      className="tw-rounded-full"
                      onClick={checkPromoCode}
                    >
                      {ValidatePromotionCodeStatus.isLoading && (
                        <>
                          <Spinner
                            animation="border"
                            role="status"
                            className="tw-w-2 tw-h-2 tw-mx-3"
                          ></Spinner>{" "}
                          {t("BillingCreatePage/t-10")}
                        </>
                      )}
                      {!ValidatePromotionCodeStatus.isLoading && (
                        <>{t("BillingCreatePage/btn-03")}</>
                      )}
                    </Button>
                  </div>
                </Fade>
              )}
            </div>

            <div className="tw-flex sm:tw-flex-row tw-flex-col tw-items-center tw-justify-between tw-gap-8 tw-my-6 tw-w-full md:tw-w-auto">
              <div className="tw-self-stretch tw-justify-between tw-items-center tw-flex lg:tw-flex-row tw-flex-col tw-gap-5 lg:tw-gap-0">
                <div className="tw-text-center"></div>
                <div className="tw-w-full md:tw-w-auto lg:tw-h-10 tw-h-full tw-p-0.5 tw-bg-gray-100 tw-rounded-full tw-justify-center tw-items-center tw-gap-0.5 tw-flex ">
                  <div
                    onClick={() => {
                      setPlanType("monthly");
                    }}
                    className={`tw-cursor-pointer tw-grow tw-shrink tw-basis-0 tw-h-9 tw-px-6 tw-py-2 ${
                      planType === "monthly"
                        ? "tw-bg-white tw-shadow-sm"
                        : "tw-bg-gray-100"
                    } tw-rounded-full  tw-border tw-border-gray-200/10 tw-justify-center tw-items-center tw-flex tw-overflow-hidden`}
                  >
                    <div className="tw-text-gray-500 tw-text-sm tw-font-normal tw-leading-tight">
                      {t("home/SiteFindBestPlan/btn-02")}
                    </div>
                  </div>
                  <div
                    onClick={() => {
                      setPlanType("annually");
                    }}
                    className={`tw-cursor-pointer tw-grow tw-shrink tw-basis-0 tw-h-9 tw-px-6 tw-py-2 ${
                      planType === "annually"
                        ? "tw-bg-white tw-shadow-sm"
                        : "tw-bg-gray-100"
                    } tw-rounded-full  tw-border tw-border-gray-200/10 tw-justify-center tw-items-center tw-flex tw-overflow-hidden`}
                  >
                    <div className="tw-text-gray-700 tw-text-sm tw-font-normal tw-leading-tight">
                      {t("home/SiteFindBestPlan/btn-01")}
                    </div>
                  </div>
                </div>
              </div>

              <div className="tw-flex tw-items-center tw-justify-center tw-gap-4">
                <Form.Check
                  type={"radio"}
                  label={`USD`}
                  id={`usd-currency`}
                  name="currency"
                  onChange={() => setCurrency("USD")}
                  checked={currency === "USD"}
                />

                <Form.Check
                  type={"radio"}
                  label={`EUR`}
                  id={`eur-currency`}
                  name="currency"
                  onChange={() => setCurrency("EUR")}
                  checked={currency === "EUR"}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="tw-container tw-bg-white  md:tw-mt-20 tw-mt-5 md:tw-p-0 tw-p-2">
          <div className="find-best-plan-body tw-relative tw-shadow-2xl md:px-8 px-4 lg:tw-overflow-hidden tw-overflow-auto tw-py-6 lg:tw-block tw-hidden tw-mb-10">
            <div className="tw-absolute tw-top-6 tw-bottom-0 tw-flex tw-w-full tw-z-[10]">
              <div
                aria-hidden="true"
                className={`tw-flex ${
                  hoveredIndex > 0 ? "tw-w-[20%] tw-px-2" : "tw-w-0"
                } `}
                style={{
                  marginLeft: `calc(${hoveredIndex * 20}% - ${
                    hoveredIndex == 1 ? 19 : 35
                  }px)`
                  // '20%',
                }}
              >
                <div
                  className={`tw-bg-gray-100 tw-w-full ${
                    hoveredIndex > 0
                      ? "tw-rounded-md tw-border tw-border-solid tw-border-gray-200"
                      : ""
                  }`}
                ></div>
              </div>
            </div>

            <table className="md:tw-table-fixed tw-text-left tw-border-separate tw-w-full tw-relative tw-z-10">
              <colgroup>
                <col className="tw-w-1/5" />
                <col className="tw-w-1/5" />
                <col className="tw-w-1/5" />
                <col className="tw-w-1/5" />
                <col className="tw-w-1/5" />
              </colgroup>
              <thead>
                <tr>
                  <td></td>
                  {plans
                    .filter((dr) => dr.type == planType)
                    .map((plan, index) => (
                      <td
                        key={index}
                        onMouseMove={() => {
                          setHoverIndex(index + 1);
                        }}
                        // scope="col"
                        className="tw-pl-8 tw-pr-8 tw-pt-8 tw-align-top"
                        // onClick={() => setHoveredIndex(index + 1)}
                      >
                        <div
                          className={`tw-flex tw-flex-col ${
                            planType === "annually"
                              ? "tw-h-[200px]"
                              : "tw-h-[150px]"
                          }  tw-gap-2`}
                        >
                          <span className="tw-font-semibold">{plan.name}</span>

                          {planType === "annually" &&
                          (plan.price["monthly"] as number) > 0 ? (
                            <div className="tw-text-xl tw-font-normal tw-relative">
                              <div className="tw-flex tw-gap-4">
                                <span>
                                  {currency === "USD" && "$"}
                                  {plan.price["monthly"]}
                                  {currency === "EUR" && "€"}
                                </span>
                              </div>
                              <span className="tw-strikethrough tw-absolute tw-top-1/2 tw-left-0 tw-w-[30px] tw-tw-bg-gray-500 tw-h-px tw-bg-current tw-rotate-[-45deg]"></span>
                            </div>
                          ) : (
                            <React.Fragment>
                              {planType === "annually" && (
                                <span className="tw-h-[28px]" />
                              )}
                            </React.Fragment>
                          )}

                          <div className={`tw-text-4xl tw-font-bold`}>
                            {plan.price[planType] !== null ? (
                              <span className="tw-flex tw-items-center">
                                {currency === "USD" && "$"}
                                {plan.price[planType]}
                                {currency === "EUR" && "€"}{" "}
                                <sub className="tw-text-sm tw-ml-2">
                                  /{t("Pricing/key37")}
                                </sub>
                              </span>
                            ) : null}
                          </div>

                          {planType === "annually" &&
                            (plan.price["monthly"] as number) > 0 && (
                              <span className="tw-text-[11px]">
                                {t("Pricing/key40")}{" "}
                                <b>
                                  {currency === "USD" && "$"}
                                  {`${(plan.price[planType] as number) * 12}`}
                                  {currency === "EUR" && "€"}{" "}
                                </b>
                                {t("Pricing/key41")}.
                              </span>
                            )}

                          <Button
                            variant=""
                            disabled={
                              isLoading !== null ||
                              (useRedirectToPayModStatus.originalArgs?.plan ===
                                plan.code[currency] &&
                                useRedirectToPayModStatus.isLoading)
                            }
                            className={`${
                              hoveredIndex === index + 1
                                ? "tw-bg-primary tw-text-white tw-border-blue-700 hover:tw-bg-blue-700 hover:tw-text-white hover:tw-border-blue-700"
                                : "tw-bg-white tw-border-gray-300 tw-text-blue-700 hover:tw-bg-blue-700 hover:tw-text-white hover:tw-border-blue-700"
                            } tw-border tw-flex tw-gap-3 tw-border-solid tw-items-center tw-justify-center tw-mt-auto`}
                            onClick={() => buyPlan(plan.code[currency])}
                          >
                            {plan.actionBtnText}
                            {isLoading && isLoading === plan.code[currency] && (
                              <Spinner animation="border" size="sm" />
                            )}
                          </Button>
                        </div>
                      </td>
                    ))}
                </tr>
              </thead>
              <tbody>
                {categories.map((item, index) => (
                  <React.Fragment key={index}>
                    <tr>
                      <th
                        scope="colgroup"
                        colSpan={5}
                        className="tw-font-[600] tw-text-sm tw-pt-16 tw-pb-4 tw-border-0 tw-border-b tw-border-gray-200 tw-border-solid"
                      >
                        {item.label}
                      </th>
                    </tr>

                    {plans
                      ?.filter((dr) => dr.type == planType)[0]
                      ?.features.filter((f) => f.category === item.value)
                      ?.map((feature, index) => (
                        <tr key={index}>
                          <td
                            className={`tw-font-[300] tw-text-sm tw-py-4 tw-border-0 tw-border-b tw-border-gray-100 tw-border-solid`}
                          >
                            {feature.name}
                          </td>
                          {plans
                            .filter((dr) => dr.type == planType)
                            .map((plan, index) => {
                              const target = plan.features.find(
                                (x) =>
                                  x.name === feature.name &&
                                  x.category == item.value
                              );

                              if (target) {
                                return (
                                  <td
                                    key={index}
                                    onMouseMove={() => {
                                      setHoverIndex(index + 1);
                                    }}
                                    className="tw-py-4 tw-border-0 tw-border-b tw-border-gray-200 tw-border-solid"
                                  >
                                    <center>
                                      {target.valueType === "boolean" ? (
                                        target.value ? (
                                          <BiCheck
                                            size={26}
                                            className="tw-text-blue-500"
                                          />
                                        ) : (
                                          <BiMinus
                                            size={20}
                                            className="tw-text-gray-500"
                                          />
                                        )
                                      ) : (
                                        <div className="tw-font-[300] tw-text-sm tw-w-[180px] ">
                                          {target.value}
                                        </div>
                                      )}
                                    </center>
                                  </td>
                                );
                              }
                            })}

                          {new Array(
                            4 - plans.filter((dr) => dr.type == planType).length
                          )
                            .fill(null)
                            .map((_, index) => (
                              <td
                                key={index}
                                className="tw-py-4 tw-border-0 tw-border-b tw-border-gray-200 tw-border-solid"
                              ></td>
                            ))}
                        </tr>
                      ))}
                  </React.Fragment>
                ))}
                <tr>
                  <td></td>
                  {plans
                    .filter((dr) => dr.type == planType)
                    .map((plan, index) => (
                      <td
                        key={index}
                        onMouseMove={() => {
                          setHoverIndex(index + 1);
                        }}
                        // scope="col"
                        className="tw-pl-8 tw-pr-8 tw-pt-8 tw-align-top"
                        // onClick={() => setHoveredIndex(index + 1)}
                      >
                        <div
                          className={`tw-flex tw-flex-col  tw-h-10 tw-gap-2`}
                        >
                          <Button
                            variant=""
                            disabled={
                              isLoading !== null ||
                              (useRedirectToPayModStatus.originalArgs?.plan ===
                                plan.code[currency] &&
                                useRedirectToPayModStatus.isLoading)
                            }
                            className={`${
                              hoveredIndex === index + 1
                                ? "tw-bg-primary tw-text-white tw-border-blue-700 hover:tw-bg-blue-700 hover:tw-text-white hover:tw-border-blue-700"
                                : "tw-bg-white tw-border-gray-300 tw-text-blue-700 hover:tw-bg-blue-700 hover:tw-text-white hover:tw-border-blue-700"
                            } tw-border tw-flex tw-gap-3 tw-border-solid tw-items-center tw-justify-center tw-mt-auto`}
                            onClick={() => buyPlan(plan.code[currency])}
                          >
                            {plan.actionBtnText}
                            {isLoading && isLoading === plan.code[currency] && (
                              <Spinner animation="border" size="sm" />
                            )}
                          </Button>
                        </div>
                      </td>
                    ))}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Small screens plans */}
          <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-mt-8 lg:tw-hidden">
            {plans
              .filter((dr) => dr.type == planType)
              .map((plan, index) => (
                <div
                  key={index}
                  className={`sm:tw-col-span-6 tw-col-span-12 tw-mb-4 tw-border tw-border-gray-200 tw-rounded-lg tw-p-6 ${
                    hoveredIndex === index + 1 ? "tw-bg-gray-100" : ""
                  }`}
                >
                  <div className="tw-flex tw-flex-col tw-items-start tw-gap-2">
                    <span className="tw-font-semibold">{plan.name}</span>

                    {planType === "annually" &&
                    (plan.price["monthly"] as number) > 0 ? (
                      <span className="tw-text-xl tw-font-normal tw-relative">
                        {currency === "USD" && "$"}
                        {plan.price["monthly"]}
                        {currency === "EUR" && "€"}
                        <span className="tw-strikethrough tw-absolute tw-top-1/2 tw-left-0 tw-w-[30px] tw-tw-bg-gray-500 tw-h-px tw-bg-current tw-rotate-[-45deg]"></span>
                      </span>
                    ) : null}

                    <div className={`tw-text-4xl tw-font-bold`}>
                      {plan.price[planType] !== null ? (
                        <span className="tw-flex tw-items-center">
                          {currency === "USD" && "$"}
                          {plan.price[planType]}
                          {currency === "EUR" && "€"}{" "}
                          <sub className="tw-text-sm tw-ml-2">
                            /{t("Pricing/key37")}
                          </sub>
                        </span>
                      ) : null}
                    </div>

                    {planType === "annually" &&
                      (plan.price["monthly"] as number) > 0 && (
                        <span className="tw-text-[11px]">
                          {t("Pricing/key40")}{" "}
                          <b>
                            {currency === "USD" && "$"}
                            {`${(plan.price[planType] as number) * 12}`}
                            {currency === "EUR" && "€"}{" "}
                          </b>
                          {t("Pricing/key41")}.
                        </span>
                      )}

                    <Button
                      disabled={
                        isLoading !== null ||
                        (useRedirectToPayModStatus.originalArgs?.plan ===
                          plan.code[currency] &&
                          useRedirectToPayModStatus.isLoading)
                      }
                      variant=""
                      className={`${
                        hoveredIndex === index + 1
                          ? "tw-bg-primary tw-text-white tw-border-blue-700 hover:tw-bg-blue-700 hover:tw-text-white hover:tw-border-blue-700"
                          : "tw-bg-white tw-border-gray-300 tw-text-blue-700 hover:tw-bg-blue-700 hover:tw-text-white hover:tw-border-blue-700"
                      } tw-border tw-flex tw-gap-3 tw-border-solid tw-items-center tw-justify-center tw-w-full`}
                      onClick={() => buyPlan(plan.code[currency])}
                    >
                      {plan.actionBtnText}

                      {isLoading && isLoading === plan.code[currency] && (
                        <Spinner animation="border" size="sm" />
                      )}
                    </Button>
                  </div>

                  <ul className="tw-mt-6">
                    {plan.features
                      .filter((f) => f.value !== false)
                      .map((feature, index) => (
                        <li
                          key={index}
                          className="tw-flex tw-items-center tw-mt-4"
                        >
                          <BiCheck
                            size={26}
                            className="tw-text-primary tw-mr-2 tw-shrink-0"
                          />
                          <span className="tw-text-sm tw-font-[300]">
                            {feature.name}{" "}
                            {feature.value && typeof feature.value !== "boolean"
                              ? `(${feature.value})`
                              : ""}
                          </span>
                        </li>
                      ))}
                  </ul>
                </div>
              ))}
          </div>
        </div>
      </section>
    );

  return <React.Fragment>{content}</React.Fragment>;
};

export default BillingCreatePage;
